import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../../services/api/client';
import TransactionInsightsView from '../../components/Admin/AccountTransactions/TransactionInsightsView';
import type {
  AccountContext,
  TransactionRow,
  TransactionSummary,
} from '../../components/Admin/AccountTransactions/types';

interface AccountTransactionsLocationState {
  accountContext?: AccountContext;
}

interface TransactionDateRange {
  since?: string;
  until?: string;
}

const AccountTransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId } = useParams();
  const locationContext = (location.state as AccountTransactionsLocationState | null)?.accountContext ?? null;
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [summary, setSummary] = useState<TransactionSummary | null>(null);
  const [accountContext, setAccountContext] = useState<AccountContext | null>(locationContext);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<TransactionDateRange>({});

  const handleDateRangeChange = useCallback((nextRange: TransactionDateRange) => {
    setDateRange((currentRange) => {
      if (currentRange.since === nextRange.since && currentRange.until === nextRange.until) {
        return currentRange;
      }
      return nextRange;
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTransactions = async () => {
      if (!accountId) {
        setError('Missing account ID');
        setTransactions([]);
        setLoading(false);
        setHasLoaded(true);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setFilterError(null);

        const query = new URLSearchParams();
        if (dateRange.since) query.set('since', dateRange.since);
        if (dateRange.until) query.set('until', dateRange.until);
        const queryString = query.toString();

        const response = await apiFetch(`/admin/accounts/${accountId}/transactions${queryString ? `?${queryString}` : ''}`, {
          method: 'GET',
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;
        setTransactions(Array.isArray(response?.data) ? response.data : []);
        setSummary(response?.summary || null);
        setAccountContext(response?.accountContext || locationContext || null);
      } catch (err: any) {
        if (controller.signal.aborted) return;
        const message = err?.message || 'Failed to fetch transactions';
        if (dateRange.since || dateRange.until) {
          setFilterError(message);
        } else {
          setError(message);
          setTransactions([]);
          setSummary(null);
          setAccountContext(locationContext);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setHasLoaded(true);
        }
      }
    };

    fetchTransactions();
    return () => controller.abort();
  }, [accountId, dateRange.since, dateRange.until, locationContext]);

  if (loading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-[#F8F7F2] p-6">
        <div className="max-w-7xl mx-auto min-h-[70vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
            <span className="text-sm text-gray-600 font-family-messina-sans">Loading transactions…</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !accountId) {
    return (
      <div className="min-h-screen bg-[#F8F7F2] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-red-600 font-family-messina-sans">{error || 'Missing account ID'}</div>
        </div>
      </div>
    );
  }

  return (
    <TransactionInsightsView
      accountId={accountId}
      accountContext={accountContext}
      transactions={transactions}
      summary={summary}
      onDateRangeChange={handleDateRangeChange}
      dateRangeRequestError={filterError}
      onBack={() => navigate('/customer-details')}
    />
  );
};

export default AccountTransactionsPage;
